import { createRoute } from "@tanstack/react-router";
import { useState, useEffect, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Upload } from "lucide-react";
import { rootRoute } from "./__root";
import { api } from "../lib/api";
import { useAuth } from "../lib/use-auth";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Input } from "../components/ui/Input/Input";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import styles from "./PriceList.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/price-list",
	component: PriceListPage,
});

interface PriceListItem {
	id: string;
	model: string;
	engineBrand: string | null;
	standbyKva: string | null;
	primeKva: string | null;
	motorModel: string | null;
	currency: string | null;
	prices: Record<string, number>;
	priceOrder?: string[];
}

const formatNumber = (v: number | string | null): string => {
	if (v == null || v === "") return "";
	const n = Number(v);
	if (!Number.isFinite(n)) return String(v);
	return n.toLocaleString("tr-TR", { maximumFractionDigits: 2 });
};

function PriceListPage() {
	const { t } = useTranslation();
	const { user } = useAuth();
	const canEdit = user?.role === "admin";

	const [brands, setBrands] = useState<string[]>([]);
	const [brand, setBrand] = useState<string>("");
	const [search, setSearch] = useState("");
	const [items, setItems] = useState<PriceListItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [uploading, setUploading] = useState(false);
	const [status, setStatus] = useState<{ type: "success" | "error"; msg: string } | null>(null);

	const loadBrands = useCallback(async () => {
		const list = await api.get<string[]>("/api/price-list/brands");
		setBrands(list);
		setBrand((prev) => (prev && list.includes(prev) ? prev : (list[0] ?? "")));
	}, []);

	useEffect(() => {
		void (async () => {
			try {
				await loadBrands();
			} catch (e) {
				setStatus({ type: "error", msg: (e as Error).message });
			} finally {
				setLoading(false);
			}
		})();
	}, [loadBrands]);

	useEffect(() => {
		let cancelled = false;
		const params = new URLSearchParams({ limit: "2000" });
		if (brand) params.set("brand", brand);
		if (search.trim()) params.set("search", search.trim());
		void (async () => {
			setLoading(true);
			try {
				const rows = await api.get<PriceListItem[]>(`/api/price-list?${params.toString()}`);
				if (!cancelled) setItems(rows);
			} catch (e) {
				if (!cancelled) setStatus({ type: "error", msg: (e as Error).message });
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [brand, search]);

	const sortedItems = useMemo(() => {
		const kva = (i: PriceListItem) => {
			const v = Number(i.standbyKva ?? i.primeKva);
			return Number.isFinite(v) ? v : Number.POSITIVE_INFINITY;
		};
		return [...items].sort((a, b) => kva(a) - kva(b));
	}, [items]);

	// Excel'deki fiyat sütunları markadan markaya değiştiği için kolonlar satırlardan türetilir.
	const priceColumns = useMemo(() => {
		const cols: string[] = [];
		const seen = new Set<string>();
		for (const item of items) {
			const labels =
				item.priceOrder && item.priceOrder.length > 0
					? item.priceOrder
					: Object.keys(item.prices ?? {});
			for (const label of labels) {
				if (!seen.has(label)) {
					seen.add(label);
					cols.push(label);
				}
			}
		}
		return cols;
	}, [items]);

	const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;
		setUploading(true);
		setStatus(null);
		try {
			const fd = new FormData();
			fd.append("file", file);
			const res = await api.upload<{ imported: number; sheets: string[] }>(
				"/api/price-list/upload",
				fd,
			);
			setStatus({
				type: "success",
				msg: `${res.imported} ${t("priceRowsImported")} (${res.sheets.length} ${t("sheets")})`,
			});
			await loadBrands();
		} catch (err) {
			setStatus({ type: "error", msg: (err as Error).message });
		} finally {
			setUploading(false);
			e.target.value = "";
		}
	};

	return (
		<div className={styles.page}>
			<PageHeader title={t("priceList")} subtitle={t("priceListHint")} />

			{status && (
				<div className={status.type === "success" ? styles.success : styles.error}>
					{status.msg}
				</div>
			)}

			<Card>
				<div className={styles.toolbar}>
					<div className={styles.tabs}>
						{brands.map((b) => (
							<button
								key={b}
								type="button"
								className={[styles.tab, b === brand && styles.tabActive].filter(Boolean).join(" ")}
								onClick={() => setBrand(b)}
							>
								{b}
							</button>
						))}
					</div>
					<div className={styles.toolbarRight}>
						<Input
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder={t("searchModel")}
						/>
						{canEdit && (
							<label className={styles.uploadBtn}>
								<Upload size={16} /> {uploading ? t("uploading") : t("uploadExcel")}
								<input
									type="file"
									accept=".xlsx,.xls"
									onChange={onUpload}
									disabled={uploading}
									hidden
								/>
							</label>
						)}
					</div>
				</div>

				{loading ? (
					<LoadingSpinner />
				) : items.length === 0 ? (
					<p className={styles.empty}>{t("priceListEmpty")}</p>
				) : (
					<div className={styles.tableWrap}>
						<table className={styles.table}>
							<thead>
								<tr>
									<th className={styles.sticky}>{t("model")}</th>
									<th>{t("motorModel")}</th>
									<th>Standby (kVA)</th>
									<th>Prime (kVA)</th>
									<th>{t("currency")}</th>
									{priceColumns.map((c) => (
										<th key={c} className={styles.numeric}>
											{c}
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{sortedItems.map((item) => (
									<tr key={item.id}>
										<td className={styles.sticky}>
											<strong>{item.model}</strong>
										</td>
										<td>{item.motorModel ?? ""}</td>
										<td className={styles.numeric}>{formatNumber(item.standbyKva)}</td>
										<td className={styles.numeric}>{formatNumber(item.primeKva)}</td>
										<td>{item.currency ?? ""}</td>
										{priceColumns.map((c) => (
											<td key={c} className={styles.numeric}>
												{formatNumber(item.prices?.[c] ?? null)}
											</td>
										))}
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</Card>
		</div>
	);
}
